import _isNil from 'lodash/isNil';
import { TFunction } from 'i18next';

export interface LegendRangeItem {
  from?: number | string;
  to?: number | string;
  color?: {
    value?: any;
    label?: any;
  };
}

export default function validateLegendRanges(
  formValues: any[],
  t: TFunction<'translation', undefined>,
): string {
  if (!Array.isArray(formValues)) return '';

  // Collect incomplete rows and valid numeric ranges
  const incompleteRows: number[] = [];
  const ranges: Array<{
    from: number;
    to: number;
    label: string;
    index: number;
    openEnded?: boolean;
  }> = [];

  // Determine the last index that has any meaningful input (from/to or color)
  let lastRelevantIndex = -1;
  for (let i = formValues.length - 1; i >= 0; i -= 1) {
    const v = formValues[i];
    if (v) {
      const hasFromV = !_isNil(v.from) && `${v.from}` !== '';
      const hasToV = !_isNil(v.to) && `${v.to}` !== '';
      const hasColorV =
        v.color && (!_isNil(v.color.value) || !_isNil(v.color.label));
      if (hasFromV || hasToV || hasColorV) {
        lastRelevantIndex = i;
        break;
      }
    }
  }

  if (lastRelevantIndex === -1) return '';

  let openEndedIndex: number | null = null;

  formValues.forEach((value, index) => {
    if (!value) return;

    const fromRaw = value.from;
    const toRaw = value.to;
    const hasFrom = !_isNil(fromRaw) && `${fromRaw}` !== '';
    const hasTo = !_isNil(toRaw) && `${toRaw}` !== '';
    const isLast = index === lastRelevantIndex;

    if (hasFrom && hasTo) {
      const fromNum = Number(fromRaw);
      const toNum = Number(toRaw);
      if (Number.isFinite(fromNum) && Number.isFinite(toNum)) {
        ranges.push({
          from: fromNum,
          to: toNum,
          label: `${fromRaw}–${toRaw}`,
          index,
        });
      }
    } else if (!hasFrom && hasTo) {
      // Incomplete row: missing 'from'
      const row = index + 1;
      if (!incompleteRows.includes(row)) incompleteRows.push(row);
    } else if (hasFrom && !hasTo) {
      // Allow empty 'to' for the last item (open-ended)
      if (!isLast) {
        const row = index + 1;
        if (!incompleteRows.includes(row)) incompleteRows.push(row);
      } else {
        const fromNum = Number(fromRaw);
        if (Number.isFinite(fromNum)) {
          ranges.push({
            from: fromNum,
            to: Number.POSITIVE_INFINITY,
            label: `${fromRaw}–`,
            index,
            openEnded: true,
          });
          openEndedIndex = index;
        }
      }
    } else if (!hasFrom && !hasTo) {
      // Treat rows with color as incomplete even if trailing; ignore rows truly empty
      const hasColor =
        value.color &&
        (!_isNil(value.color.value) || !_isNil(value.color.label));
      if (index <= lastRelevantIndex || hasColor) {
        const row = index + 1;
        if (!incompleteRows.includes(row)) incompleteRows.push(row);
      }
    }
  });

  // If there's an open-ended range, any subsequent rows are invalid
  if (openEndedIndex !== null) {
    for (let i = openEndedIndex + 1; i < formValues.length; i += 1) {
      const v = formValues[i];
      if (v) {
        // Report the row index as missing value to indicate invalid trailing range
        const row = i + 1;
        if (!incompleteRows.includes(row)) incompleteRows.push(row);
      }
    }
  }

  // Detect overlapping ranges
  const overlapSet = new Set<number>();
  const sorted = [...ranges].sort((a, b) => a.from - b.from);
  let prev: (typeof ranges)[number] | null = null;
  sorted.forEach((r) => {
    if (prev && r.from <= prev.to) {
      overlapSet.add(prev.index);
      overlapSet.add(r.index);
      // Extend the previous boundary to support chaining overlaps
      if (r.to > prev.to) {
        prev = { ...r, from: prev.from };
      }
    } else {
      prev = r;
    }
  });

  const overlappingLabels = sorted
    .filter((r) => overlapSet.has(r.index))
    .map((r) => r.label);

  // Detect numeric gaps between consecutive ranges
  const gapLabels: string[] = [];
  if (sorted.length) {
    // Start from the first, set prev correctly
    let prevRange = sorted[0];
    for (let i = 1; i < sorted.length; i += 1) {
      const cur = sorted[i];
      const prevEffectiveTo = prevRange.to;
      if (
        Number.isFinite(prevEffectiveTo) &&
        cur.from > (prevEffectiveTo as number) + 1
      ) {
        const start = (prevEffectiveTo as number) + 1;
        const end = cur.from - 1;
        gapLabels.push(start === end ? `${start}` : `${start}–${end}`);
      }
      // Merge continuous or overlapping ranges to extend coverage tracking
      if (cur.from <= prevRange.to || cur.from === prevRange.to + 1) {
        // extend if current goes further
        if (cur.to > prevRange.to) {
          prevRange = { ...prevRange, to: cur.to };
        }
      } else {
        prevRange = cur;
      }
    }
  }

  const parts: string[] = [];
  if (overlappingLabels.length) {
    parts.push(`${overlappingLabels.join(', ')} (${t('overlap')})`);
  }
  if (gapLabels.length) {
    parts.push(
      `${gapLabels.map((item) => `${item}`).join(', ')} (${t('validation-ranges-missing-value')})`,
    );
  }
  if (incompleteRows.length) {
    parts.push(
      `${incompleteRows.map((n) => `#${n}`).join(', ')} (${t('validation-invalid-range')})`,
    );
  }

  return parts.length
    ? `${t('validation-invalid-ranges')}: ${parts.join(', ')}`
    : '';
}
